import React, { useMemo, useState } from "react";
import { Checkbox } from "../../components/Checkbox";
import { PendingChip } from "../../components/PendingChip";
import { BILLING_METHOD_LABELS } from "../../constants/billing";
import { SR_ONLY } from "../../constants/ui";

// The advertiser filter of the QuickBooks page's CSV tabs: which advertisers go
// into the file, whatever their billing method. `selected` is null for every advertiser
// (the default; on the Invoice export it also covers advertisers the list does not know), or a Set
// of advertiser ids.

const quick = {
  padding: "6px 12px", background: "transparent", color: "#3E3E3E", border: "1px solid #CFCFCF",
  borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer",
};
const quickActive = { ...quick, background: "#FFE9F6", color: "#A3005F", borderColor: "#F90091" };

function sameIds(selected, ids) {
  return selected !== null && selected.size === ids.length && ids.every((id) => selected.has(id));
}

function QuickFilter({ active, disabled = false, onClick, children }) {
  return (
    <button type="button" aria-pressed={active} disabled={disabled} style={{ ...(active ? quickActive : quick), opacity: disabled ? 0.5 : 1 }} onClick={onClick}>
      {children}
    </button>
  );
}

export function QuickBooksAdvertiserPicker({ advertisers, selected, onChange, error }) {
  const [search, setSearch] = useState("");
  const autoChargeIds = useMemo(() => (advertisers ?? []).filter((a) => a.autoCharge).map((a) => a.id), [advertisers]);
  const cardIds = useMemo(() => (advertisers ?? []).filter((a) => a.billingMethod === "credit_card").map((a) => a.id), [advertisers]);
  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (advertisers ?? []).filter((a) => !term || a.name.toLowerCase().includes(term) || String(a.id).includes(term));
  }, [advertisers, search]);

  const total = advertisers?.length ?? 0;
  const autoChargeActive = sameIds(selected, autoChargeIds);
  const cardActive = sameIds(selected, cardIds);
  const isChecked = (id) => selected === null || selected.has(id);
  const toggle = (id) => {
    const next = new Set(selected ?? advertisers.map((a) => a.id));
    if (next.has(id)) next.delete(id);
    else next.add(id);
    // Ticking every advertiser by hand is the same as All advertisers (null).
    onChange(next.size === total ? null : next);
  };

  return (
    <fieldset style={{ border: "none", padding: 0, margin: "0 0 18px" }}>
      <legend style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#3E3E3E", marginBottom: 6, padding: 0 }}>Advertisers</legend>
      {error ? (
        <p role="alert" style={{ fontSize: 12, color: "#B42318", margin: 0 }}>{`${error} The export includes every advertiser.`}</p>
      ) : advertisers === null ? (
        <p style={{ fontSize: 12, color: "#626262", margin: 0 }}>Loading the advertisers…</p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
            <QuickFilter active={selected === null} onClick={() => onChange(null)}>All advertisers</QuickFilter>
            <QuickFilter active={cardActive} disabled={cardIds.length === 0} onClick={() => onChange(new Set(cardIds))}>
              Card advertisers
            </QuickFilter>
            <QuickFilter active={autoChargeActive} disabled={autoChargeIds.length === 0} onClick={() => onChange(new Set(autoChargeIds))}>
              Auto-charge accounts
            </QuickFilter>
            <QuickFilter active={selected !== null && selected.size === 0} onClick={() => onChange(new Set())}>Clear</QuickFilter>
            <span aria-live="polite" style={{ fontSize: 12, color: "#626262", fontVariantNumeric: "tabular-nums" }}>
              {selected === null ? `All ${total} advertisers` : `${selected.size} of ${total} advertisers`}
            </span>
          </div>
          <label htmlFor="advertiser-search" style={SR_ONLY}>
            Search advertisers
          </label>
          <input
            id="advertiser-search"
            type="search"
            placeholder="Search by name or id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ padding: "8px 10px", border: "1px solid #CFCFCF", borderRadius: 8, fontSize: 13, width: "100%", maxWidth: 360, boxSizing: "border-box", marginBottom: 8 }}
          />
          <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #E2E2E2", borderRadius: 8, padding: "6px 10px" }}>
            {shown.length === 0 && <p style={{ fontSize: 12, color: "#626262", margin: "4px 0" }}>No advertiser matches.</p>}
            {shown.map((a) => (
              <label key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#151515", padding: "4px 0", cursor: "pointer" }}>
                <Checkbox checked={isChecked(a.id)} onChange={() => toggle(a.id)} />
                <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{a.name}</span>
                <span style={{ fontSize: 11, color: "#8A8A8A", fontVariantNumeric: "tabular-nums" }}>{a.id}</span>
                {/* No billing method still exports (its conversions are billable), so flag it for accounting. */}
                {a.billingMethod
                  ? <PendingChip tone="neutral" label={BILLING_METHOD_LABELS[a.billingMethod] ?? a.billingMethod} />
                  : <PendingChip tone="warning" label="No billing method" />}
                {a.autoCharge && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#A3005F", background: "#FFE9F6", borderRadius: 999, padding: "1px 8px" }}>Auto-charge</span>
                )}
              </label>
            ))}
          </div>
        </>
      )}
    </fieldset>
  );
}
